import { ScoredStock, StockFactors, PillarScores } from './compute';
import { rescoreStocks, ALL_QUALITY_KEYS, ALL_VALUATION_KEYS } from './clientScore';

export type ProjectionHorizon = 'now' | '+1Y' | '+2Y';

/**
 * Project stock valuations forward in time using consensus estimates.
 * Quality (X-axis) stays the same — only valuation (Y-axis) changes.
 *
 * +1Y: Use 2yr estimates as the new 1yr estimates (price / fwdEps2yr, EV / fwdRev2yr)
 * +2Y: Extrapolate 3yr estimates assuming same growth rate continues
 */
export function projectStocks(
  stocks: ScoredStock[],
  horizon: ProjectionHorizon,
  enabledQuality?: Set<keyof StockFactors>,
  enabledValuation?: Set<keyof StockFactors>,
  pillarWeights?: Record<keyof PillarScores, number>,
): ScoredStock[] {
  if (horizon === 'now') return stocks;

  // Step 1: Modify valuation factors for each stock based on projection
  const projected = stocks.map(s => {
    const f = { ...s.factors };

    const eps1 = f.fwdPE !== null && f.fwdPE > 0 ? f.fwdPE : null; // current fwd P/E 1yr
    const eps2 = f.fwdPE2yr;  // current fwd P/E 2yr
    const evRev1 = f.fwdEvToRevenue;

    if (horizon === '+1Y') {
      // Shift: 2yr estimate becomes the new 1yr
      if (eps2 !== null) {
        f.fwdPE = eps2;       // price/eps2yr is now the "1yr forward" P/E
        // Extrapolate a new 2yr: assume same eps growth continues
        if (eps1 !== null && eps1 > 0 && eps2 < eps1) {
          // Growth rate = eps1/eps2 implies eps is growing (lower PE = higher EPS)
          // eps_3yr P/E = eps2yr^2 / eps1yr (growth rate continues)
          f.fwdPE2yr = eps2 > 0 ? (eps2 * eps2) / eps1 : null;
        } else {
          f.fwdPE2yr = eps2; // no growth extrapolation possible
        }
      }
      // Shift fwd EV/Revenue similarly
      if (evRev1 !== null && f.fwdEvToRevenue !== null) {
        // We don't have explicit fwdEvToRevenue2yr in factors,
        // but we can estimate: if fwdRevCagr2yr exists, use it
        const revGrowth = f.fwdRevCagr2yr;
        if (revGrowth !== null && revGrowth > -1) {
          f.fwdEvToRevenue = evRev1 / (1 + revGrowth);
        }
      }
      // Adjust PEG: new fwdPE / growth
      if (f.fwdPE !== null && f.fwdEpsGrowth !== null && f.fwdEpsGrowth > 0) {
        const cappedGrowth = Math.min(f.fwdEpsGrowth * 100, 50);
        f.pegRatio = cappedGrowth > 0 ? f.fwdPE / cappedGrowth : null;
      }
    } else if (horizon === '+2Y') {
      // Extrapolate to 3yr forward
      if (eps1 !== null && eps2 !== null && eps1 > 0 && eps2 > 0) {
        // Growth rate of PE compression: ratio = eps2/eps1
        const ratio = eps2 / eps1;
        f.fwdPE = eps2 * ratio;       // 3yr forward P/E
        f.fwdPE2yr = f.fwdPE > 0 ? f.fwdPE * ratio : null; // 4yr extrapolation
      } else if (eps2 !== null) {
        f.fwdPE = eps2;
        f.fwdPE2yr = eps2;
      }
      // EV/Revenue: compound the growth
      if (evRev1 !== null) {
        const revGrowth = f.fwdRevCagr2yr;
        if (revGrowth !== null && revGrowth > -1) {
          f.fwdEvToRevenue = evRev1 / ((1 + revGrowth) * (1 + revGrowth));
        }
      }
      // PEG
      if (f.fwdPE !== null && f.fwdEpsGrowth !== null && f.fwdEpsGrowth > 0) {
        const cappedGrowth = Math.min(f.fwdEpsGrowth * 100, 50);
        f.pegRatio = cappedGrowth > 0 ? f.fwdPE / cappedGrowth : null;
      }
    }

    return { ...s, factors: f };
  });

  // Step 2: Re-run Z-score normalization for VALUATION ONLY.
  // Quality score is pinned to its current value — only the valuation (Y-axis) shifts.
  // This prevents the global Z-score recalibration from dragging quality scores around.
  const eQ = enabledQuality ?? new Set(ALL_QUALITY_KEYS);
  const eV = enabledValuation ?? new Set(ALL_VALUATION_KEYS);
  const rescored = rescoreStocks(projected, eQ, eV, pillarWeights);

  // Restore original quality scores — only valuation changes in projection mode
  return rescored.map((s, i) => ({
    ...s,
    qualityScore: stocks[i].qualityScore,
    pillarScores: {
      ...s.pillarScores,
      profitability: stocks[i].pillarScores.profitability,
      growth: stocks[i].pillarScores.growth,
      safety: stocks[i].pillarScores.safety,
      shareholderReturns: stocks[i].pillarScores.shareholderReturns,
    },
  }));
}
